import commonMixin from '../../mixins/_common';
import modalMixin from '../../mixins/_modal';
import flashMessageMixin from '../../mixins/_flash_messages';
import period from '../../components/period/_index';
import imageSelecter from '../../components/_image_selecter';

// 期間のフォーマット
const FORMAT = 'YYYY/MM/DD HH:mm';
// 期間の正規表現フォーマット
const REGEX = /20\d\d\/[0-1]\d\/[0-3]\d [0-2]\d:[0-5]\d/;
// 取得するデータのキー
const COL_NAME = 'price_history';
// computed用関数
const SET_COMPUTED_VALUE = function (key) {
    return {
        get() {
            return `${this.item_data[key] ?? ''}`;
        },
        set(value) {
            this.item_data[key] = value;
        }
    };
};

/**
 * セール価格設定切り替えコンポーネント
 */
export default {
    name: 'priceEditor',
    template: '#price_editor_component_template',
    data () {
        return {
            // 現在のmomentオブジェクト
            now: moment(),
            // sale価格設定欄を表示するフラグ
            show: false,
            // セール設定タブフラグ RESERVE_SALE|NORMAL_SALE
            sale_type: 'RESERVE_SALE',
            // 期間設定バリデーションメッセージ再描画用フラグ
            show_period_validation_message: true,
            // 実績一覧
            price_histories: [],
            // 定価(メーカー希望小売価格)設定欄を表示するフラグ
            original_price_show: false,
        };
    },
    props: {
        // 再編集画面か
        is_edit_item: {
            type: Boolean,
            required: true,
        },
        // 商品データ
        item_data: {
            type: Object,
            default: () => ({}),
            required: true,
        },
        // 表在庫か否か
        is_table_stock_type: {
            type: Boolean,
            required: true,
        },
        //「個別商品価格」を設定するフラグ
        prop_variant_price_show: {
            type: Boolean,
            required: true,
        },
    },
    mixins: [
        commonMixin,
        modalMixin,
        flashMessageMixin,
    ],
    components: {
        // 期間指定コンポーネント
        'period': period,
        // エビデンス画像の選択コンポーネント
        'image-selecter': imageSelecter,
        'modal-variant-price-confirm': {
            template: '#modal_variant_price_confirm_template',
            mixins: [modalMixin],
            emits: ['cancel', 'ok'],
            methods: {
                handleCancel: function () {
                    this.$emit('cancel');
                    this.close();
                },
                handleOk: function () {
                    this.$emit('ok');
                    this.close();
                }
            }
        }
    },
    computed: {
        // このコンポーエントで使う値をthis.item_dataから定義
        price: SET_COMPUTED_VALUE('price'),
        salePrice: SET_COMPUTED_VALUE('sale_price'),
        memberPrice: SET_COMPUTED_VALUE('member_price'),
        periodStart: SET_COMPUTED_VALUE('sale_period_start'),
        periodEnd: SET_COMPUTED_VALUE('sale_period_end'),
        reservePrice: SET_COMPUTED_VALUE('reserve_price'),
        reserveSalePrice: SET_COMPUTED_VALUE('reserve_sale_price'),
        reserveMemberPrice: SET_COMPUTED_VALUE('reserve_member_price'),
        reservePeriodStart: SET_COMPUTED_VALUE('reserve_selling_period_start'),
        reservePeriodEnd: SET_COMPUTED_VALUE('reserve_selling_period_end'),
        originalPrice: SET_COMPUTED_VALUE('original_price'),
        originalPriceEvidence: SET_COMPUTED_VALUE('original_price_evidence'),
        // 商品データがセットされているか
        hasItemData: function () {
            return (Object.keys(this.item_data).length > 0);
        },
        // 商品コード
        itemCode: function () {
            return (this.hasItemData) ? this.item_data.item_code : null;
        },
        // 表示用の販売実績
        dispPriceHistories: function () {
            let histories = [];
            for (const i in this.price_histories) {
                let history = this.price_histories[i];
                // 未チェックフラグはスキップ
                if (Number(history.check) === 0) {
                    continue;
                }
                //                const start = (history.lasttime) ? moment(history.lasttime) : this.now;
                const start = this.now.clone().format(FORMAT);
                let end = (history.lasttime) ? createMoment(history.lasttime) : this.now.clone();
                end = end.add(2, 'weeks').format(FORMAT);
                histories.push({
                    price: history.price,
                    start: start,
                    end: end,
                });
            }
            return histories;
        },
        // 実績リストにセール設定可がある
        priceHistoriesInCheckFlag: function () {
            return (this.dispPriceHistories.length > 0);
        },
        // セール価格設定が可能か
        canSalePrice: function () {
            if (this.hasItemData) {
                // 通常セール設定がされている
                if (this.salePrice && this.periodStart && this.periodEnd) {
                    return true;
                }
                // 自動切り替え設定がされている
                if (this.reservePeriodStart && this.reservePeriodEnd) {
                    if (this.reservePrice || this.reserveSalePrice) {
                        return true;
                    }
                }
            }
            // 実績リストにセール設定可がある
            if (this.priceHistoriesInCheckFlag) {
                return true;
            }
            return false;
        },
        // 最大開始日時
        maxStartSaleDate: function () {
            return this.maxStartDate(this.periodStart, this.periodEnd, this.price, this.salePrice);
        },
        // 最大切り替え開始日時
        maxStartReserveDate: function () {
            return this.maxStartDate(this.reservePeriodStart, this.reservePeriodEnd, this.reservePrice, this.reserveSalePrice);
        },
        // priceバリデーション
        priceValidMessage: function () {
            const value = this.price;
            if (value.length) {
                if (this.periodStart || this.periodEnd || this.salePrice) {
                    if (this.priceHistoriesInCheckFlag && !this.maxStartSaleDate) {
                        return '通常価格を「販売実績」と合わせましょう';
                    }
                }
            }
            return '';
        },
        // sale_priceバリデーション
        salePriceValidMessage: function () {
            const value = this.salePrice;
            if (value.length) {
                if (Number(value) >= Number(this.price)) {
                    return '通常価格より安い金額にしましょう';
                }
            } else {
                if (this.periodStart || this.periodEnd) {
                    return 'セール価格も入れましょう';
                }
            }
            return '';
        },
        // reserve_priceバリデーション
        reservePriceValidMessage: function () {
            const value = this.reservePrice;
            if (this.reservePeriodStart || this.reservePeriodEnd) {
                if (value.length) {
                    if (this.reserveSalePrice && this.priceHistoriesInCheckFlag && !this.maxStartReserveDate) {
                        return '通常価格を、販売実績と合わせましょう';
                    }
                } else {
                    return '通常価格も入れましょう';
                }
            }
            return '';
        },
        // reserve_sale_priceバリデーション
        reserveSalePriceValidMessage: function () {
            if (this.reserveSalePrice && this.reservePrice) {
                if (Number(this.reserveSalePrice) >= Number(this.reservePrice)) {
                    return '通常価格より安い金額にしましょう';
                }
            }
            return '';
        },
        // periodバリデーション
        periodValidMessage: function () {
            return this.periodValidCommonMessage(
                this.periodStart,
                this.periodEnd,
                this.salePrice,
                this.maxStartSaleDate,
                '販売期間'
            );
        },
        // reserve_periodバリデーション
        reservePeriodValidMessage: function () {
            const message = this.periodValidCommonMessage(
                this.reservePeriodStart,
                this.reservePeriodEnd,
                this.reserveSalePrice,
                this.maxStartReserveDate,
                '切替期間'
            );
            if (message) {
                return message;
            }

            if (this.reserveSalePrice && !this.reservePrice) {
                return 'セール価格がある場合は、通常価格も入れましょう';
            }
            // 切り替え通常価格が入力されていたら
            if (this.reservePrice) {
                if (!this.reservePeriodStart) {
                    return 'セール期間の「開始日時」を入れましょう';
                }
                if (!this.reservePeriodEnd) {
                    return 'セール期間の「終了日時」を入れましょう';
                }
                const start = createMoment(this.reservePeriodStart);
                const end = createMoment(this.reservePeriodEnd);
                const diff = end.diff(start);
                // msと比較
                if (diff < 0) {
                    return '「終了日時」は「開始日時」より後にしましょう';
                }
                if (diff > (28 * 24 * 60 * 60 * 1000)) {
                    return 'セール期間は「28日間」までとなります';
                }
                if (diff < (2 * 60 * 60 * 1000)) {
                    return 'セール期間は「2時間以上」からとなります';
                }
            }

            // 販売期間、切り替え期間両方設定されていた場合
            if (this.salePrice && (this.reservePrice || this.reserveSalePrice)) {
                const saleStart = createMoment(this.periodStart);
                const saleEnd = createMoment(this.periodEnd);
                const reserveStart = createMoment(this.reservePeriodStart);
                const reserveEnd = createMoment(this.reservePeriodEnd);
                if (reserveStart.isBefore(saleStart) || reserveEnd.isAfter(saleEnd)) {
                    return '切替期間は、「販売期間」内にしてください';
                }
            }
            return '';
        },
        // original_priceバリデーション
        originalPriceValidMessage: function () {
            const value = this.originalPrice;
            if (!value?.length && this.originalPriceEvidence?.length) {
                return 'メーカー希望小売価格も入れましょう';
            }
            return '';
        },
        // original_price_evidenceバリデーション
        originalPriceEvidenceValidMessage: function () {
            const value = this.originalPriceEvidence;
            if (!value?.length && this.originalPrice?.length) {
                return 'エビデンス画像も入れましょう';
            }
            return '';
        },
        // 販売期間カレンダーの開始日用選択不可設定値
        periodStartDisabledDates: function () {
            return this.commonDisabledDates(this.price);
        },
        // 切り替え期間カレンダーの開始日用選択不可設定値
        reservePeriodStartDisabledDates: function () {
            // 販売期間、切り替えセール価格が指定されていたらそれを使う
            if (this.periodStart && this.periodEnd && this.periodStartDisabledDates.to) {
                if (this.reserveSalePrice) {
                    return this.periodStartDisabledDates;
                }
                return {
                    to: createMoment(this.periodStart),
                    from: createMoment(this.periodEnd),
                };
            }
            // 切り替えセール価格が設定されていたら
            if (this.reserveSalePrice) {
                return this.commonDisabledDates(this.reservePrice);
            }
            return {
                to: null,
                from: null,
            };
        },
        // 販売期間カレンダーの終了日用選択不可設定値
        periodEndDisabledDates: function () {
            if (this.periodStart) {
                return {
                    to: createMoment(this.periodStart).add(2, 'hours'),
                    from: createMoment(this.periodStart).add(14, 'days'),
                };
            }
            return {
                to: null,
                from: null,
            };
        },
        // 切り替え期間カレンダーの終了日用選択不可設定値
        reservePeriodEndDisabledDates: function () {
            // 販売期間、切り替えセール価格が指定されていたらそれを使う
            if (this.periodStart && this.periodEnd) {
                return {
                    to: (this.reservePeriodStart) ? createMoment(this.reservePeriodStart) : createMoment(this.periodStart),
                    from: createMoment(this.periodEnd),
                };
            }
            if (this.reservePeriodStart) {
                const date = createMoment(this.reservePeriodStart);
                if (date) {
                    const fromOffset = (this.reserveSalePrice) ? 14 : 28;
                    return {
                        to: date.clone().add(2, 'hours'),
                        from: date.clone().add(fromOffset, 'days'),
                    };
                }
            }
            return {
                to: null,
                from: null,
            };
        },
        // 税込か否か
        isTaxIncluded: function () {
            // 税設定の取得
            const data = document.querySelector('[data-roll="tax_data"]');
            const taxData = (!!data && !!JSON.parse(data.innerHTML)) ? JSON.parse(data.innerHTML)['data'] : null;
            // 税込の真偽値を返す
            return !taxData || Number(taxData.tax_included);
        },
        //「個別商品価格」を設定するフラグ
        variant_price_show: {
            get() {
                return this.prop_variant_price_show;
            },
            set(value) {
                this.$emit('update:prop_variant_price_show', value);
            },
        },
        // セール価格設定に入力された欄があるか
        hasAnySalePriceConfig: function () {
            return [
                this.salePrice,
                this.memberPrice,
                this.periodStart,
                this.periodEnd,
                this.reservePrice,
                this.reserveSalePrice,
                this.reservePeriodStart,
                this.reservePeriodEnd,
                this.reserveMemberPrice,
            ].some(Boolean);
        }
    },
    watch: {
        item_data: {
            async handler() {
                // バリデーションメッセージを更新する
                this.updateValidationMessage();
            },
            deep: true,
        },
        'item_data.stock_config.type': function (type) {
            if (type !== 'table') {
                this.variant_price_show = false;
            }
        },
        // セール設定欄表示フラグ
        show: async function (boolean) {
            if (boolean) {
                // セール不可状態だったら販売実績確認モーダルを出す
                if (!this.canSalePrice) {
                    this.open();
                }
                this.variant_price_show = false;
                return;
            }
            // トグルOFF時 セールの入力が一つでもあったらアラート
            if (!boolean &&
                (this.hasAnySalePriceConfig)
            ) {
                if (await swalConfirm('セール価格設定が初期化されます。よろしいですか？')) {
                    this.clearSalePriceConfig();
                } else {
                    this.show = true;
                }
                return;
            }
        },
        // セール設定タブを切り替えた時バリデーションメッセージを更新する
        sale_type: function () {
            this.updateValidationMessage();
        },
        // 定価(メーカー希望小売価格)設定欄表示フラグ
        original_price_show: async function (boolean) {
            // トグルOFF時 定価入力が一つでもあったらアラート
            if (!boolean &&
                (this.originalPrice || this.originalPriceEvidence)
            ) {
                if (await swalConfirm('定価(メーカー希望小売価格)設定が初期化されます。よろしいですか？')) {
                    this.originalPrice = '';
                    this.originalPriceEvidence = '';
                } else {
                    this.original_price_show = true;
                }
            }
        },
        // 「個別商品価格」設定欄表示フラグ
        variant_price_show: function (isChecked) {
            if (isChecked) {
                // ON時にセール価格設定があればリセット
                if (this.hasAnySalePriceConfig) {
                    this.$refs.variantPriceModal.open();
                    this.clearSalePriceConfig();
                }
            } else {
                // OFF時は個別商品価格をすべてリセット
                this.item_data.subcodes_config.values
                    .flat()
                    .forEach(cell => {
                        cell.price = null;
                    });
            }
        },
    },
    created: function() {
        // 拡張機能からデータを取得完了したらpostMessageで通知される
        window.addEventListener('message', (e) => {
            if (typeof e.data.action === 'undefined' || e.data.action !== 'get_storecreator_data' || COL_NAME !== e.data.col_name) {
                return;
            }
            // キーの指定があった場合はerrorの場合なので抜ける
            if (typeof e.data.key !== 'undefined') {
                return;
            }
            this.setPriceHistory(e.data.data);
            this.close();
            this.$nextTick(() => {
                if (!this.priceHistoriesInCheckFlag) {
                    swal('景品表示法に沿った販売実績がないため、この商品はセール価格を設定できません。');
                }
            });
        });
    },
    mounted() {
        // 個別商品価格が設定されていたら「サイズやカラー別に価格を設定する」をONにする
        this.variant_price_show = this.hasAnyStockPrice();
        // 定価(メーカー希望小売価格)が設定されていたら設定欄を表示する
        this.original_price_show = !!this.originalPrice;
        // セール価格設定が可能だったら設定欄を表示させる
        this.show = this.canSalePrice;
        // タブの初期表示(通常セール設定がされていて切替設定が無い場合はタブを「NORMAL_SALE」にする)
        if (this.show) {
            this.sale_type = 'RESERVE_SALE';
            if (this.salePrice && this.periodStart && this.periodEnd) {
                if (!this.reservePrice && !this.reserveSalePrice && !this.reservePeriodStart && !this.reservePeriodEnd) {
                    this.sale_type = 'NORMAL_SALE';
                }
            }
        }
    },
    emits: ['update:prop_variant_price_show'],
    methods: {
        // バリデーションメッセージを更新する
        updateValidationMessage: async function () {
            await this.$nextTick();
            this.setCustomMessage('price', this.priceValidMessage);
            this.setCustomMessage('sale_price', this.salePriceValidMessage);
            this.setCustomMessage('reserve_price', this.reservePriceValidMessage);
            this.setCustomMessage('reserve_sale_price', this.reserveSalePriceValidMessage);
            this.setCustomMessage('original_price', this.originalPriceValidMessage);
            this.setCustomMessage('original_price_evidence', this.originalPriceEvidenceValidMessage);
            await this.reShowPeriodValidationMessage();
        },
        // 期間設定バリデーションメッセージ再描画
        reShowPeriodValidationMessage: async function () {
            this.show_period_validation_message = false;
            await this.$nextTick();
            this.show_period_validation_message = true;
        },
        // バリデーションエラーがあるタブへ切り替える(親から実行される想定)
        changeValidErrorTab: async function () {
            if (this.priceValidMessage || this.salePriceValidMessage || this.periodValidMessage) {
                this.sale_type = 'NORMAL_SALE';
            } else if (this.reservePriceValidMessage || this.reserveSalePriceValidMessage || this.reservePeriodValidMessage) {
                this.sale_type = 'RESERVE_SALE';
            }
            await this.$nextTick();
            console.log('changeValidErrorTab: ' + this.sale_type);
        },
        // 期間のバリデーションメッセージ共通メソッド
        periodValidCommonMessage: function (start, end, salePrice, maxStartDate, prefixMessage) {
            if (salePrice.length) {
                if (!start.length) {
                    return prefixMessage + 'の「開始日時」を入れましょう';
                }
                if (!end.length) {
                    return prefixMessage + 'の「終了日時」を入れましょう';
                }
                if (!REGEX.test(start)) {
                    return prefixMessage + 'の「開始日時」の形式が正しくありません。例) 2020/01/01 09:00';
                }
                if (!REGEX.test(end)) {
                    return prefixMessage + 'の「終了日時」の形式が正しくありません。例) 2020/01/01 09:00';
                }
                start = createMoment(start);
                end = createMoment(end);
                const diff = end.diff(start);
                if (this.priceHistoriesInCheckFlag) {
                    if (!maxStartDate) {
                        // 通常価格でバリデーションエラーになる想定
                        return '';
                    }
                    if (start.isAfter(maxStartDate)) {
                        return prefixMessage + '「開始日時」は' + maxStartDate.format(FORMAT) + 'より前にしましょう';
                    }
                    //                  const minStartDate = maxStartDate.clone().subtract(2, 'weeks').format(FORMAT);
                    const minStartDate = this.now.clone().format(FORMAT);
                    if (start.isBefore(minStartDate)) {
                        return prefixMessage + '「開始日時」は' + minStartDate + 'より後にしましょう';
                    }
                }
                // msと比較
                if (diff < 0) {
                    return prefixMessage + '「終了日時」は「開始日時」より後にしましょう';
                }
                if (diff > (14 * 24 * 60 * 60 * 1000)) {
                    return prefixMessage + 'は14日間までにしましょう';
                }
                if (diff < (2 * 60 * 60 * 1000)) {
                    return prefixMessage + 'は2時間以上にしましょう';
                }
            }
            return '';
        },
        // 通常価格に対する最終販売日を取得
        maxStartDate: function (start, end, price, salePrice) {
            if (start.length || end.length || salePrice.length) {
                for (const i in this.dispPriceHistories) {
                    let history = this.dispPriceHistories[i];
                    if (Number(history.price.replace(/[^0-9]/g, '')) === Number(price)) {
                        return createMoment(history.end);
                    }
                }
            }
            return '';
        },
        // 指定価格に対するカレンダー設定の選択不可設定値を返す共通メソッド
        commonDisabledDates: function (price) {
            for (const i in this.dispPriceHistories) {
                let history = this.dispPriceHistories[i];
                if (Number(history.price.replace(/[^0-9]/g, '')) === Number(price)) {
                    return {
                        to: createMoment(history.start),
                        from: createMoment(history.end),
                    };
                }
            }
            return {
                to: null,
                from: null,
            };
        },
        // 指定refにバリデーションメッセージをセットする
        setCustomMessage: async function (ref, message) {
            await this.$nextTick();
            if (this.$refs[ref] && typeof this.$refs[ref] !== 'undefined') {
                this.$refs[ref].setCustomValidity(message);
            }
        },
        // 販売実績確認モーダルを閉じたら
        closed: function () {
            if (!this.canSalePrice) {
                this.show = false;
            }
        },
        // 編集不可状態の時、入力フォームをクリックしたら実績取得モーダルを開く
        clickReadonlyInput: function () {
            if (!this.priceHistoriesInCheckFlag) {
                this.open();
            }
        },
        // 拡張機能を発動
        getPriceHistory: function () {
            this.$root.startExtension('get_' + COL_NAME, null, {'page_key': this.itemCode});
        },
        // 個別商品価格が入力されているか
        hasAnyStockPrice: function () {
            return this.item_data.subcodes_config.values
                .flat()
                .some(cell => cell?.price?.length > 0);
        },
        // 販売実績があるか(ストクリから実績取得)
        setPriceHistory: function (data) {
            let priceHistories = [];
            if (typeof data.SummarySet !== 'undefined') {
                const summaries = this.$_changeArray(data.SummarySet.Summary);
                for (const i in summaries) {
                    priceHistories.push(summaries[i]._attributes);
                }
            }
            this.price_histories = priceHistories;
            console.log(data);
            console.log(this.price_histories);
        },
        // セール価格設定欄をクリアする
        clearSalePriceConfig: function () {
            this.salePrice = '';
            this.memberPrice = '';
            this.periodStart = '';
            this.periodEnd = '';
            this.reservePrice = '';
            this.reserveSalePrice = '';
            this.reservePeriodStart = '';
            this.reservePeriodEnd = '';
            this.reserveMemberPrice = '';
        },
        // 個別商品価格設定欄をクリアする
        setVariantPriceForm: function () {
            this.show = false;
            this.clearSalePriceConfig();
        }
    }
};
